import { useVsiAppContext } from "../../stores/VsiApp";
import React, { useEffect, useState } from "react";
import { useDebounce } from "../../utils/hooks";
import { Button, Card, CardBody, CardHeader, Col, Input, Row } from "reactstrap";
import { VsiLgText, VsiMedLabel, VsiMedText, VsiSmallText } from "../../styled/App.styled";
import { withErrorBoundary } from "../../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../../boundaries/GeneralErrorBoundary";
import { numberInputOnWheelPreventChange } from "../../utils/Helpers";
import { roundToNearestHundredth } from "../../utils/math";

function HostCard({ host, activeCapacityPlan, changeDataWithModel, deleteHost, onCardClick }) {
  const calcModelCount = () => {
    return host.model_count + (activeCapacityPlan?.modelCountAdded ?? 0) ?? 0;
  };

  const { theme } = useVsiAppContext();

  const [modelCount, setModelCount] = useState(calcModelCount());

  const debouncedModelCount = useDebounce(modelCount, 250);

  const onChange = (e) => {
    const { value } = e.target;
    if (value > -1) {
      setModelCount(value);
    }
  };

  useEffect(() => {
    setModelCount(calcModelCount());
    // eslint-disable-next-line
  }, [activeCapacityPlan]);

  useEffect(() => {
    if (debouncedModelCount) {
      changeDataWithModel({
        ...host,
        modelCountAdded: modelCount - host.model_count
      });
    }
  }, [debouncedModelCount]);

  const countDiff = modelCount - host.model_count;

  let color = theme === "dark" ? "secondary" : "muted";
  if (host.isCustom) {
    color = "warning";
  }

  const onInputFocus = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const calcHostCost = () => {
    const { cost_per_cpu, cost_per_disk, cost_per_memory, memory_size, disk_size, cpus } = host;
    const cpu = ((cost_per_cpu * cpus) || 0);
    const disk = ((cost_per_disk * disk_size) || 0);
    const mem = ((cost_per_memory * memory_size) || 0);
    const total = cpu + disk + mem;

    return roundToNearestHundredth(total * modelCount).toLocaleString();
  };

  return (
    <Card color={color} className={"my-2"} style={{ cursor: "pointer" }} onClick={() => onCardClick(host)}>
      <CardHeader>
        <Row>
          <Col xs={8}>
            <div className={"d-flex flex-column"}>
              <VsiLgText textAlign={"left"}>{host?.model}</VsiLgText>
              <VsiSmallText textAlign={"left"}>* Click to edit $ per</VsiSmallText>
            </div>
            {host.isCustom ? (
              <Button color={"danger"} size={"sm"} className={"ms-1"} onClick={deleteHost}>
                <i className={"fa-solid fa-trash"} />
              </Button>
            ) : null}
          </Col>
          <Col
            xs={4}
            className={"d-flex flex-row justify-content-end align-items-center"}
          >
            <VsiSmallText
              style={{ color: countDiff === 0 ? "transparent" : null }}
            >{`${countDiff >= 0 ? "+" : ""}${countDiff || ""}`}</VsiSmallText>
            <Input
              className={"ms-2"}
              value={modelCount}
              type={"number"}
              onClick={onInputFocus}
              onFocus={onInputFocus}
              onWheel={numberInputOnWheelPreventChange}
              onChange={onChange}
            />
          </Col>
        </Row>
      </CardHeader>
      {/*<VsiHr/>*/}
      <CardBody>
        <Row>
          <Col xs={3} className={"d-flex flex-column justify-content-between"}>
            {/*think this as compute power, not count, not MHz*/}
            <VsiMedLabel textAlign={"left"}>vCPU</VsiMedLabel>
            <VsiMedText textAlign={"left"}>
              {(host.cpus * modelCount).toLocaleString()}
            </VsiMedText>
            <VsiSmallText textAlign={"left"}>(${host.cost_per_cpu} per)</VsiSmallText>
          </Col>
          <Col xs={3} className={"d-flex flex-column justify-content-between"}>
            <VsiMedLabel textAlign={"left"}>Memory</VsiMedLabel>
            <VsiMedText textAlign={"left"}>
              {parseInt(host.memory_size * modelCount).toLocaleString()}
            </VsiMedText>
            <VsiSmallText textAlign={"left"}>(${host.cost_per_memory} per)</VsiSmallText>
          </Col>
          {host.isCustom ? (
            <Col xs={3} className={"d-flex flex-column justify-content-between"}>
              <VsiMedLabel textAlign={"left"}>Disk</VsiMedLabel>
              <VsiMedText textAlign={"left"}>
                {parseInt(host.disk_size * modelCount).toLocaleString()}
              </VsiMedText>
              <VsiSmallText textAlign={"left"}>(${host.cost_per_disk} per)</VsiSmallText>
            </Col>
          ) : null}
          <Col xs={{
            offset: host.isCustom ? null : 3,
            size: 3
          }} className={"d-flex flex-column align-items-start"}>
            <VsiMedLabel textAlign={"left"}>Cost ($)</VsiMedLabel>
            <VsiMedText textAlign={"left"}>
              ${calcHostCost()}
            </VsiMedText>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}


export default withErrorBoundary(HostCard, GeneralErrorBoundary);
